import dayjs, { Dayjs } from 'dayjs';
import { IDateObject } from '@/interfaces/powerMarkets/IDateObject.ts';
import { resolveDateRange } from '@/utils/dateUtils.ts';

export function getDatesFromHeaderConfigOrOld (
    dbObject: { dateObject: IDateObject; from: string; to: string },
    fromOrTo: string,
): Dayjs | null {
    if (dbObject.dateObject) {
        const dates = getDatesFromConfig(dbObject.dateObject);
        return fromOrTo === 'from' ? dates.from : dates.to;
    }
    return fromOrTo === 'from' ? dayjs(dbObject.from) : dayjs(dbObject.to);
}

export function getTitle (
    fromDate: string,
    toDate: string,
    minimumView = 'day',
    fromHours: number | null = null,
    toHours: number | null = null,
): string {
    const format = {
        day: 'll',
        month: 'MMM YYYY',
        year: 'YYYY',
    }[minimumView];

    let title;
    if (fromHours && toHours) {
        title = `Previous ${fromHours}h to Next ${toHours}h`;
    } else {
        const dayjsFrom = dayjs(fromDate, 'DDMMYYYY');
        const dayjsTo = dayjs(toDate, 'DDMMYYYY');

        if (dayjs(toDate).isBefore(fromDate) || fromDate === toDate) {
            title = `${dayjsFrom.format(format)}`;
        } else if (
            dayjsFrom.isSame(dayjsFrom.clone().startOf('month'), 'day')
            && dayjsTo.isSame(dayjsTo.clone().endOf('month'), 'day')
            && dayjsFrom.clone().startOf('month').isSame(dayjsTo.clone().startOf('month'), 'day')
        ) {
            title = `${dayjsFrom.format('MMM YYYY')}`;
        } else if (
            dayjsFrom.isSame(dayjsFrom.clone().startOf('year'), 'day')
            && dayjsTo.isSame(dayjsTo.clone().endOf('year'), 'day')
            && dayjsFrom.clone().startOf('year').isSame(dayjsTo.clone().startOf('year'), 'day')
        ) {
            title = `${dayjsFrom.format('YYYY')}`;
        } else if (dayjsFrom.year() === dayjsTo.year()) {
            title = `${dayjsFrom.format('MMM D')} - ${dayjsTo.format('ll')}`;
        } else if (toDate == null) {
            title = dayjsFrom.format(format);
        } else {
            title = `${dayjsFrom.format(format)} - ${dayjsTo.format(format)}`;
        }
    }
    return title;
}

export function getDatesFromConfig (
    dateObject: IDateObject,
    fromDateOverride: string | Date | null = null,
    toDateOverride: string | Date | null = null,
): { from: Dayjs | null; to: Dayjs | null } {
    const fromObject = dateObject.from ?? { value: 0, type: 'missing' }; // default should never happen
    const toObject = dateObject.to ?? { value: 0, type: 'missing' }; // default should never happen

    if (fromDateOverride || toDateOverride) {
        return { from: dayjs(fromDateOverride), to: dayjs(toDateOverride) };
    }

    let { from, to } = resolveDateRange(fromObject, toObject);

    if (dateObject.recentHour) {
        if (dateObject.fromHours != null) {
            from = dayjs().subtract(dateObject.fromHours, 'hours');
        }
        if (dateObject.toHours != null) {
            to = dayjs().add(dateObject.toHours, 'hours');
        }
    }

    return { from, to };
}
